import { useState } from "react";

import type { Meta, StoryObj } from "@storybook/react";

import { fakeMultiSelectOptions } from "@/__mocks__/multiselect";
import type { MultiSelectModalProps } from "@/ui/composed/modal/MultiSelectModal/MultiSelectModal";
import MultiSelectModal from "@/ui/composed/modal/MultiSelectModal/MultiSelectModal";
import { Button, Text } from "@/ui/core";
import { HStack, VStack } from "@/ui/layout";
import { joinWithCommas } from "@/utils/string.util";

const StoryMeta: Meta<MultiSelectModalProps> = {
  title: "Components/Modal/MultiSelect Modal",
  component: MultiSelectModal,
  argTypes: {
    isSearchable: { control: "boolean" },
    isSingleSelect: { control: "boolean" },
    isSmallBottomPadding: { control: "boolean" },
    onDismiss: { action: "dismiss" },
    onValuesChange: { action: "options changed" },
    options: { control: "object" },
    shouldAllowEmptySelection: { control: "boolean" },
    title: { control: "text" },
  },
  args: {
    defaultSelectedValues: [],
    options: fakeMultiSelectOptions(10),
    title: "Select Options",
  },
  // eslint-disable-next-line no-restricted-syntax -- non-arrow function used here as React FC needed for use of state hook
  render: function Render(args) {
    const {
      defaultSelectedValues,
      isSearchable,
      isSingleSelect,
      isSmallBottomPadding,
      options,
      shouldAllowEmptySelection,
      title,
    } = args;

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [values, setValues] = useState<MultiSelectOption[]>(
      defaultSelectedValues || [],
    );

    const displayValues = values.map((option) => option.displayText);

    const handleDismiss = () => {
      setIsOpen(false);
    };

    return (
      <>
        <VStack alignItems="center" width="100%">
          <Button
            onPress={() => setIsOpen(true)}
            testID=""
            title="Open Modal"
          />
          <HStack width="100%">
            <Text testID="" variant="bodyMedium">
              Values:
            </Text>
            <Text testID="">{joinWithCommas(displayValues)}</Text>
          </HStack>
        </VStack>

        <MultiSelectModal
          defaultSelectedValues={values}
          isOpen={isOpen}
          isSearchable={isSearchable}
          isSingleSelect={isSingleSelect}
          isSmallBottomPadding={isSmallBottomPadding}
          onDismiss={handleDismiss}
          onValuesChange={(v) => setValues(v)}
          options={options}
          shouldAllowEmptySelection={shouldAllowEmptySelection}
          testID=""
          title={title}
        />
      </>
    );
  },
};

type Story = StoryObj<MultiSelectModalProps>;

const Primary: Story = {};
const SingleSelect: Story = {
  args: {
    isSingleSelect: true,
  },
};

const Searchable: Story = {
  args: {
    isSearchable: true,
  },
};

const AllowEmptySelection: Story = {
  args: {
    shouldAllowEmptySelection: true,
  },
};

const MultiSelectSearchable: Story = {
  args: {
    isSearchable: true,
  },
};

const defaultValueOptions = fakeMultiSelectOptions(5);
const WithDefaultValues: Story = {
  args: {
    defaultSelectedValues: defaultValueOptions.slice(0, 2),
    options: defaultValueOptions,
  },
};

const WithDynamicHeight: Story = {
  args: {
    options: fakeMultiSelectOptions(4),
  },
};

export default StoryMeta;
export {
  AllowEmptySelection,
  MultiSelectSearchable,
  Primary,
  Searchable,
  SingleSelect,
  WithDefaultValues,
  WithDynamicHeight,
};
